import { useState, useRef, useEffect } from "react";

// material-ui
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useMediaQuery } from "@mui/system";

// project import
import useAuth from "hooks/useAuth";
import { useGetMenuMaster } from "api/menu";
import { useGetUser } from "api/user";
import Loader from "components/Loader";
import Profile from "../../Header/HeaderContent/Profile";
import { canAccessAdminPanel } from "utils/adminAccess";
import { V2 } from "layout/Dashboard/Header/headerV2Tokens";
import { ChevronDown } from "lucide-react";

const TITLE_PREFIX = {
  PROF: "Pr",
  DOC: "Dr",
  ETU: "",
  OTHER: "",
};

/** Match veille card .v2-avatar / .v2-avatar-fallback */
const avatarSx = {
  width: 34,
  height: 34,
  borderRadius: "50%",
  objectFit: "cover",
  background: "var(--v2-surface-2)",
  border: `1px solid ${V2.line}`,
  flexShrink: 0,
};

const avatarFallbackSx = {
  ...avatarSx,
  display: "grid",
  placeItems: "center",
  color: V2.muted,
  fontFamily: V2.fontBody,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1,
};

/** Same as veille EditorialCards initials() */
function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

export default function UserList() {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { user: userData, userLoading } = useGetUser();
  const { user } = useAuth();

  const downLG = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [profileOpen, setProfileOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const profileAnchorRef = useRef(null);

  useEffect(() => {
    setAvatarFailed(false);
  }, [userData?.profile_image_url]);

  const handleToggle = () => {
    setProfileOpen((prevOpen) => !prevOpen);
  };

  const getFormattedName = (data) => {
    if (!data) return "";
    const titlePrefix = data.title ? TITLE_PREFIX[data.title] || "" : "";
    const name = data.name || "";
    return titlePrefix ? `${titlePrefix} ${name}` : `${name}`;
  };

  const displayName =
    `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim() ||
    user?.name ||
    userData?.name ||
    "";
  const avatarLetters = initials(displayName) || "?";
  const avatarSrc = userData?.profile_image_url?.trim() || "";
  const showAvatarImage = Boolean(avatarSrc) && !avatarFailed;

  if (userLoading) return <Loader />;

  if (!userData) {
    return (
      <Box sx={{ p: 1, px: !drawerOpen ? 1.25 : 2, minWidth: 270 }}>
        <Typography>No user data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", zIndex: 1300 }}>
      <Box
        ref={profileAnchorRef}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={profileOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          textWrap: "nowrap",
          minHeight: 44,
          pl: 0.25,
          pr: 0.5,
          py: 0.5,
          border: "none",
          borderRadius: 'var(--brand-radius-md)',
          bgcolor: "transparent",
          cursor: "pointer",
          transition: "background 0.2s ease, color 0.2s ease",
          "&:hover": {
            bgcolor: V2.accentSoft,
          },
        }}
      >
        {!downLG && (
          <>
            {showAvatarImage ? (
              <Box
                component="img"
                className="v2-avatar"
                src={avatarSrc}
                alt=""
                sx={avatarSx}
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <Box
                component="div"
                className="v2-avatar-fallback"
                sx={avatarFallbackSx}
                aria-hidden
              >
                {avatarLetters}
              </Box>
            )}
            <Box sx={{ minWidth: 0, mr: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: V2.ink,
                  fontFamily: V2.fontBody,
                  fontSize: "0.9rem",
                  lineHeight: 1.2,
                }}
              >
                {getFormattedName(user)}
              </Typography>
              {user?.profession_name ? (
                <Typography
                  sx={{
                    color: V2.muted,
                    fontFamily: V2.fontBody,
                    fontSize: "0.75rem",
                    lineHeight: 1.2,
                  }}
                >
                  {user.profession_name}
                </Typography>
              ) : null}
            </Box>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                color: profileOpen ? V2.accent : V2.muted,
                transform: profileOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s ease, color 0.2s ease",
                flexShrink: 0,
                ml: "auto",
              }}
            >
              <ChevronDown size={16} strokeWidth={2} />
            </Box>
          </>
        )}

        <Profile
          admin={canAccessAdminPanel(user)}
          open={profileOpen}
          setOpen={setProfileOpen}
          userData={userData}
          anchorRef={profileAnchorRef}
          downLG={downLG}
          hideTrigger
        />
      </Box>
    </Box>
  );
}
